import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const START_YEAR = 1930;
const MIN_AGE = 6;

export function isLeapYear(year: number): boolean {
  if (year % 4 !== 0) return false;
  if (year % 400 === 0) return true;
  if (year % 100 === 0) return false;
  return true;
}

/**
 * 生年月日セレクタコンポーネント
 *
 * @element kd-birthday-selector
 * @description 年/月/日の 3 つの <select> に option を動的生成し、
 *              うるう年判定・日数再計算・hidden input への値セットを行う。
 *              jQuery + onchange 属性を排除した Lit 版。
 */
@customElement('kd-birthday-selector')
export class KdBirthdaySelector extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ attribute: 'birth-year' }) birthYear = '';

  @property({ attribute: 'birth-month' }) birthMonth = '';

  @property({ attribute: 'birth-day' }) birthDay = '';

  @property({ attribute: 'birthday-input-selector' }) birthdayInputSelector = '#birthday';

  private yearSelect: HTMLSelectElement | null = null;

  private monthSelect: HTMLSelectElement | null = null;

  private daySelect: HTMLSelectElement | null = null;

  private birthdayInput: HTMLInputElement | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    this.yearSelect = this.querySelector<HTMLSelectElement>('[name="yyyy"]');
    this.monthSelect = this.querySelector<HTMLSelectElement>('[name="mm"]');
    this.daySelect = this.querySelector<HTMLSelectElement>('[name="dd"]');

    const form = this.closest('form');
    this.birthdayInput =
      form?.querySelector<HTMLInputElement>(this.birthdayInputSelector) ??
      document.querySelector<HTMLInputElement>(this.birthdayInputSelector);

    if (!this.yearSelect || !this.monthSelect || !this.daySelect) return;

    this.populateYearOptions();
    this.populateMonthOptions();
    this.populateDayOptions(DAYS_IN_MONTH[0]);

    if (this.birthYear) this.yearSelect.value = this.birthYear;
    if (this.birthMonth) this.monthSelect.value = this.birthMonth;
    if (this.birthDay) this.daySelect.value = this.birthDay;

    this.yearSelect.addEventListener('change', this.handleYearChange);
    this.monthSelect.addEventListener('change', this.handleMonthChange);
    this.daySelect.addEventListener('change', this.handleDayChange);

    this.updateBirthday();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.yearSelect?.removeEventListener('change', this.handleYearChange);
    this.monthSelect?.removeEventListener('change', this.handleMonthChange);
    this.daySelect?.removeEventListener('change', this.handleDayChange);
  }

  private populateYearOptions(): void {
    if (!this.yearSelect) return;
    const maxYear = new Date().getFullYear() - MIN_AGE;
    const fragment = document.createDocumentFragment();

    const placeholder = document.createElement('option');
    placeholder.value = 'year';
    placeholder.textContent = '年';
    fragment.appendChild(placeholder);

    for (let i = START_YEAR; i <= maxYear; i++) {
      const option = document.createElement('option');
      option.value = String(i);
      option.textContent = String(i);
      fragment.appendChild(option);
    }

    this.yearSelect.appendChild(fragment);
  }

  private populateMonthOptions(): void {
    if (!this.monthSelect) return;
    const fragment = document.createDocumentFragment();

    const placeholder = document.createElement('option');
    placeholder.value = 'month';
    placeholder.textContent = '月';
    fragment.appendChild(placeholder);

    for (let i = 1; i <= 12; i++) {
      const option = document.createElement('option');
      option.value = String(i);
      option.textContent = String(i);
      fragment.appendChild(option);
    }

    this.monthSelect.appendChild(fragment);
  }

  private populateDayOptions(maxDay: number): void {
    if (!this.daySelect) return;
    const currentValue = this.daySelect.value;

    this.daySelect.innerHTML = '';

    const fragment = document.createDocumentFragment();

    const placeholder = document.createElement('option');
    placeholder.value = 'day';
    placeholder.textContent = '日';
    fragment.appendChild(placeholder);

    for (let i = 1; i <= maxDay; i++) {
      const option = document.createElement('option');
      option.value = String(i);
      option.textContent = String(i);
      fragment.appendChild(option);
    }

    this.daySelect.appendChild(fragment);

    if (Number(currentValue) > maxDay) {
      this.daySelect.value = '1';
    } else if (currentValue && currentValue !== 'day') {
      this.daySelect.value = currentValue;
    }
  }

  private getDaysInMonth(): number {
    if (!this.monthSelect || !this.yearSelect) return DAYS_IN_MONTH[0];

    const monthStr = this.monthSelect.value;
    if (monthStr === 'month') return DAYS_IN_MONTH[0];

    const monthIndex = parseInt(monthStr, 10) - 1;
    let days = DAYS_IN_MONTH[monthIndex];

    if (monthIndex === 1 && this.yearSelect.value !== 'year') {
      if (isLeapYear(parseInt(this.yearSelect.value, 10))) {
        days = 29;
      }
    }

    return days;
  }

  private handleYearChange = (): void => {
    const days = this.getDaysInMonth();
    this.populateDayOptions(days);
    this.updateBirthday();
  };

  private handleMonthChange = (): void => {
    const days = this.getDaysInMonth();
    this.populateDayOptions(days);
    this.updateBirthday();
  };

  private handleDayChange = (): void => {
    this.updateBirthday();
  };

  private updateBirthday(): void {
    if (!this.birthdayInput || !this.yearSelect || !this.monthSelect || !this.daySelect) return;

    const year = this.yearSelect.value;
    const month = this.monthSelect.value;
    const day = this.daySelect.value;

    if (year === 'year' || month === 'month' || day === 'day') return;

    this.birthdayInput.disabled = false;
    this.birthdayInput.value = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-birthday-selector': KdBirthdaySelector;
  }
}
